/**
 * Discovery dialog — the operator-facing surface for `.deployit/` script
 * discovery.
 *
 * Two phases inside one modal:
 *
 *   1. **Preview** — fires `POST /discover` automatically when the dialog
 *      opens. Renders the proposal list grouped by component with one
 *      checkbox per row. Operator picks which to apply.
 *
 *   2. **Apply** — POST /discover/apply with the selected list, then
 *      renders per-row outcomes (applied / skipped / error with reason).
 *      The operator closes the modal; queryClient invalidates so the
 *      Application page picks up the new script rows.
 *
 * The "Configure" phase that used to live here was retired with slice 8
 * of #41 — repo location is now expressed as a repo-role Component on the
 * application (created via the create-application wizard's step 2, or via
 * the Components card). When no repo Component exists, /discover returns
 * 422 and we render a hint pointing the operator at the Components card.
 *
 * The modal stays self-contained — no external state — so opening it
 * from any page (`onOpenChange`) gets the full flow.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  GitBranch,
  AlertCircle,
  AlertTriangle,
  Check,
  X as XIcon,
  Search,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog.js';
import { Button } from '@/components/ui/Button.js';
import {
  api,
  ApiError,
  type Application,
  type DiscoveryApplyResult,
  type DiscoveryProposal,
  type PendingDiscovery,
} from '@/api/client.js';
import { cn } from '@/lib/utils.js';
import {
  DiscoveryPreview,
  defaultSelection,
  selectionKey,
  selectionsFromKeys,
} from './discovery-preview.js';

interface Props {
  application: Application;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Pending discovery row, if one is active for this application. When
   * present, the dialog surfaces the originating sha in the header so
   * the operator knows "this is the proposal set the bell badge was
   * pointing at" instead of guessing whether their click triggered a
   * fresh scan or replayed an existing one. The dialog still re-runs
   * /discover when opened (cheap clone, freshest answer); the pending
   * row is for context only.
   */
  pendingDiscovery?: PendingDiscovery | null;
}

// Selection key + payload-builder + default-select rule live in
// `discovery-preview.ts` so the wizard's final-review step can reuse
// the same identities and destructive-opt-in behaviour.

export function DiscoveryDialog({
  application,
  open,
  onOpenChange,
  pendingDiscovery,
}: Props): JSX.Element {
  const qc = useQueryClient();

  // --- Preview phase: auto-runs when the modal opens ---
  // The 422 error path ("No discovery source for this application. Add a
  // repo Component...") renders as a friendly hint pointing at the
  // Components card — see the error block in the preview body below.
  const discoverQuery = useQuery({
    queryKey: ['discover', application.id],
    queryFn: () => api.applications.discover(application.id),
    enabled: open,
    retry: false, // discovery errors are operator-actionable; don't loop
    staleTime: 0,
  });

  // --- Selection state ---
  // `defaultSelection` pre-ticks create + update, leaves remove off.
  // Centralised in discovery-preview.ts so the wizard's final-review
  // step starts with the same destructive-opt-in stance.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!discoverQuery.data) return;
    setSelected(defaultSelection(discoverQuery.data.proposals));
  }, [discoverQuery.data]);

  function toggle(key: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function selectByPredicate(pred: (p: DiscoveryProposal) => boolean): void {
    if (!discoverQuery.data) return;
    const next = new Set<string>();
    for (const p of discoverQuery.data.proposals) {
      if (p.kind === 'noop') continue;
      if (pred(p)) next.add(selectionKey(p));
    }
    setSelected(next);
  }

  // --- Apply phase ---
  const [applyResult, setApplyResult] = useState<DiscoveryApplyResult | null>(null);
  const apply = useMutation({
    mutationFn: async () => {
      const selections = selectionsFromKeys(selected, discoverQuery.data?.proposals ?? []);
      if (selections.length === 0) {
        throw new Error('No proposals selected.');
      }
      return api.applications.discoverApply(application.id, selections);
    },
    onSuccess: async (result) => {
      setApplyResult(result);
      // Refresh anything that might surface the new rows. The
      // pendingDiscovery + notifications queries refresh too so the
      // banner + bell badge clear immediately (#45 slice 5 stamps
      // pending_discoveries.applied_at server-side; this invalidation
      // makes the SPA reflect that without waiting for the 30s poll).
      await qc.invalidateQueries({ queryKey: ['scripts'] });
      await qc.invalidateQueries({ queryKey: ['script'] });
      await qc.invalidateQueries({ queryKey: ['applications'] });
      await qc.invalidateQueries({ queryKey: ['pendingDiscovery'] });
      await qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  function reset(): void {
    setApplyResult(null);
    setSelected(new Set());
    discoverQuery.refetch();
  }

  function closeAndReset(): void {
    setApplyResult(null);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Discover scripts from <span className="font-mono">.deployit/</span>
          </DialogTitle>
          <DialogDescription>
            DeployIt clones the configured repo, walks{' '}
            <span className="font-mono">.deployit/components/&lt;slug&gt;/scripts.yaml</span>,
            compares against this application's current scripts, and proposes
            adds / updates / removes. Nothing is written until you confirm.
          </DialogDescription>
          {/* Trigger context: when there's a pending_discoveries row,
              tell the operator which scan they're looking at. Helps
              disambiguate "I clicked Review from the banner" vs "I
              opened this fresh from the application menu". */}
          {pendingDiscovery && (
            <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Search className="h-3 w-3" />
              Reviewing changes detected from sha{' '}
              <span className="font-mono">{pendingDiscovery.fetchedSha.slice(0, 7)}</span>
              {' · '}
              <span title={pendingDiscovery.detectedAt}>
                {new Date(pendingDiscovery.detectedAt).toLocaleString()}
              </span>
            </p>
          )}
        </DialogHeader>

        {/* ---------- Preview phase ---------- */}
        {!applyResult && (
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-end gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
              <Button variant="ghost" size="sm" onClick={() => discoverQuery.refetch()}>
                {discoverQuery.isFetching ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Search className="h-3 w-3" />
                )}
                Re-scan
              </Button>
            </div>

            {discoverQuery.isFetching && !discoverQuery.data && (
              <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}

            {discoverQuery.error && (
              <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {discoverQuery.error instanceof ApiError
                  ? discoverQuery.error.message
                  : 'Discovery failed.'}
              </p>
            )}

            {discoverQuery.data && (
              <DiscoveryPreview
                report={discoverQuery.data}
                selected={selected}
                toggle={toggle}
                onSelectAll={() => selectByPredicate(() => true)}
                onSelectByKind={(k) => selectByPredicate((p) => p.kind === k)}
                onSelectNone={() => setSelected(new Set())}
              />
            )}

            {/* Selection count gets bound to the Apply button label so the
                operator sees the magnitude of what they're about to do
                ("Apply 12 selected") before clicking. Disabled state is
                styled by the Button component when there's nothing to apply. */}
            <DialogFooter className="pt-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button
                onClick={() => apply.mutate()}
                disabled={
                  !discoverQuery.data ||
                  selectionsFromKeys(selected, discoverQuery.data.proposals).length === 0 ||
                  apply.isPending
                }
              >
                {apply.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {(() => {
                  const n = discoverQuery.data
                    ? selectionsFromKeys(selected, discoverQuery.data.proposals).length
                    : 0;
                  return n > 0 ? `Apply ${n} selected` : 'Apply selected';
                })()}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ---------- Apply result phase ---------- */}
        {applyResult && (
          <div className="space-y-3 py-2">
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm">
              Applied {applyResult.totals.applied}, skipped {applyResult.totals.skipped},
              errors {applyResult.totals.errors}.
            </div>
            <div className="max-h-[40vh] overflow-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="border-b border-border bg-muted/30 text-left">
                  <tr>
                    <th className="px-2 py-1.5 font-medium">Component / Script</th>
                    <th className="px-2 py-1.5 font-medium">Action</th>
                    <th className="px-2 py-1.5 font-medium">Outcome</th>
                    <th className="px-2 py-1.5 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {applyResult.results.map((r, i) => (
                    <tr key={i} className="border-b border-border last:border-b-0">
                      <td className="px-2 py-1.5 font-mono">
                        {r.componentSlug}/{r.scriptSlug}
                      </td>
                      <td className="px-2 py-1.5">{r.action}</td>
                      <td className="px-2 py-1.5">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium',
                            r.outcome === 'applied'
                              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                              : r.outcome === 'skipped'
                                ? 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400'
                                : 'border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400',
                          )}
                        >
                          {r.outcome === 'applied' && <Check className="h-3 w-3" />}
                          {r.outcome === 'skipped' && <AlertTriangle className="h-3 w-3" />}
                          {r.outcome === 'error' && <XIcon className="h-3 w-3" />}
                          {r.outcome}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground">{r.reason ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter className="pt-2">
              <Button variant="ghost" onClick={reset}>
                Discover again
              </Button>
              <Button onClick={closeAndReset}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
